// src/page/SummaryPointEdit.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';

export default function SummaryPointEdit() {
    useAuthGuard();
    const navigate = useNavigate();
    const { pointActionID } = useParams();

    // ===== helpers =====
    const pad2 = (n) => String(n).padStart(2, '0');
    const fmtDisp = (d) =>
        `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    const fmtFromISO = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        return isNaN(d) ? '' : fmtDisp(d);
    };

    // ===== labels =====
    const STATUS_LABELS = { "0": "予約", "1": "確定" };
    const TYPE_LABELS = {
        "0": "初期ポイント",
        "1": "起動ポイント",
        "2": "行動ポイント",
        "3": "クーポン利用",
        "4": "交通ICカード連携",
        "5": "シェアサイクル連携",
        "6": "BPサービス連携",
        "7": "becoz連携ポイント",
        "8": "キャンペーン応募",
    };

    // ===== form state =====
    const [pointActionId, setPointActionId] = useState('');
    const [eventId, setEventId] = useState('');
    const [spotId, setSpotId] = useState('');
    const [userNumber, setUserNumber] = useState('');
    const [actedAtDisp, setActedAtDisp] = useState('');
    const [pointActionType, setPointActionType] = useState('');
    const [pointActionTypeDetail, setPointActionTypeDetail] = useState('');
    const [actionNote, setActionNote] = useState('');
    const [amount, setAmount] = useState('');
    const [pointStatus, setPointStatus] = useState('0');

    // Datetime picker (flatpickr)
    const actionFpRef = useRef(null);
    const [actionImplementDate, setActionImplementDate] = useState(null); // Date|null
    const actionImplementText = actionImplementDate ? fmtDisp(actionImplementDate) : '';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // validation UI
    const [errors, setErrors] = useState({});
    const invalid = (k) => (errors[k] ? 'is-invalid' : '');
    const feedback = (k) => (errors[k] ? <div className="invalid-feedback d-block">{errors[k]}</div> : null);
    const scrollToFirstError = (errObj) => {
        const first = Object.keys(errObj)[0];
        if (!first) return;
        const node =
            document.querySelector(`[data-error-key="${first}"]`) ||
            document.getElementById(first) ||
            document.querySelector(`[name="${first}"]`);
        node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // ===== init flatpickr =====
    useEffect(() => {
        if (!window.flatpickr) return;
        const fp = window.flatpickr('#action_implement_time', {
            enableTime: true,
            time_24hr: true,
            dateFormat: 'Y/m/d H:i',
            allowInput: false,
            locale: window.flatpickr.l10ns?.ja || 'ja',
            onChange: (dates) => {
                const next = dates?.[0] ?? null;
                // update only when actually different
                setActionImplementDate(prev => {
                    const prevTs = prev ? prev.getTime() : null;
                    const nextTs = next ? next.getTime() : null;
                    return prevTs === nextTs ? prev : next;
                });
            }
        });
        actionFpRef.current = fp;
        return () => { try { fp.destroy(); } catch { } };
    }, []);

    useEffect(() => {
        if (!actionFpRef.current) return;
        if (actionImplementDate) actionFpRef.current.setDate(actionImplementDate, false);
        else { try { actionFpRef.current.clear(); } catch { } }
    }, [actionImplementDate]);

    // ===== load detail =====
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError('');
                const token = localStorage.getItem('access_token') || '';
                const res = await fetch(`${API_BASE_URL}/v1/summary/point/${encodeURIComponent(pointActionID)}`, {
                    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    signal: ac.signal
                });
                if (!res.ok) {
                    let msg = `HTTP ${res.status}`;
                    try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
                    throw new Error(msg);
                }
                const j = await res.json();
                const it = j?.item || {};

                setPointActionId(it.pointActionId || '');
                setEventId(it.eventId || '');
                setSpotId(it.spotId || '');
                setUserNumber(it.userNumber || '');
                setActedAtDisp(fmtFromISO(it.actedAt));

                setPointActionType((it.pointActionType ?? '').toString());
                setPointActionTypeDetail(it.pointActionTypeDetail ?? '');
                setActionNote(it.actionNote ?? '');
                setAmount((it.amount ?? '').toString());
                setPointStatus((it.pointStatus ?? '0').toString());

                if (it.actionImplementTime) {
                    const d = new Date(it.actionImplementTime);
                    if (!isNaN(d)) setActionImplementDate(d);
                } else {
                    setActionImplementDate(null);
                }
            } catch (e) {
                if (e.name !== 'AbortError') setError('読み込みに失敗しました');
            } finally {
                setLoading(false);
            }
        })();
        return () => ac.abort();
    }, [pointActionID]);

    // ===== validate =====
    const validate = () => {
        const err = {};
        if (!pointActionType) err.pointActionType = '操作区分は必須です';
        if (!actionNote.trim()) err.actionNote = '操作摘要は必須です';

        // amount: required integer
        if (amount === '' || amount === null) {
            err.amount = 'ポイント数は必須です';
        } else {
            const n = Number(amount);
            if (!Number.isFinite(n) || !Number.isInteger(n)) {
                err.amount = 'ポイント数は整数で入力してください';
            }
        }

        if (!['0', '1'].includes(pointStatus)) {
            err.pointStatus = 'ポイントステータスが不正です';
        }

        setErrors(err);
        return err;
    };

    const onCancel = () => navigate('/summary/point');

    // ===== submit (PUT) =====
    const onSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;

        const err = validate();
        if (Object.keys(err).length) {
            scrollToFirstError(err);
            return;
        }

        try {
            setSaving(true);

            // Build payload per SummaryPointUpdateHandler (camelCase, inside { item: {} })
            const payload = {
                item: {
                    pointActionType,
                    pointActionTypeDetail: pointActionTypeDetail ?? '',
                    actionNote: actionNote.trim(),
                    amount: Number(amount),
                    pointStatus,
                    // send RFC3339; send empty string to CLEAR when null
                    actionImplementTime: actionImplementDate ? actionImplementDate.toISOString() : ""
                }
            };

            const token = localStorage.getItem('access_token') || '';
            const res = await fetch(`${API_BASE_URL}/v1/summary/point/${encodeURIComponent(pointActionID)}`, {
                method: 'PUT',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                let msg = `更新に失敗しました (HTTP ${res.status})`;
                try { const j = await res.json(); if (j?.error) msg = j.error; } catch { }
                throw new Error(msg);
            }

            const j = await res.json().catch(() => ({}));
            // Optional: refresh from response.item if provided
            if (j?.item) {
                const it = j.item;
                setPointActionType((it.pointActionType ?? pointActionType).toString());
                setPointActionTypeDetail(it.pointActionTypeDetail ?? pointActionTypeDetail);
                setActionNote(it.actionNote ?? actionNote);
                setAmount(((it.amount ?? amount) + '').toString());
                setPointStatus((it.pointStatus ?? pointStatus).toString());
                if ('actionImplementTime' in it) {
                    if (it.actionImplementTime) {
                        const d = new Date(it.actionImplementTime);
                        setActionImplementDate(isNaN(d) ? null : d);
                    } else {
                        setActionImplementDate(null);
                    }
                }
            }

            alert('更新しました。');
            navigate('/summary/point');
        } catch (err2) {
            alert(err2?.message || '更新に失敗しました');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <>
                <nav className="mb-3" aria-label="breadcrumb">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item"><a href="/summary">実績管理</a></li>
                        <li className="breadcrumb-item"><a href="/summary/point">ポイント実績</a></li>
                        <li className="breadcrumb-item active">ポイント修正</li>
                    </ol>
                </nav>

                <form className="mb-9" onSubmit={onSubmit}>
                    <div className="row g-3 flex-between-end mb-5">
                        <div className="col-auto"><h2 className="mb-2">ポイント修正</h2></div>
                        <div className="col-auto">
                            <button className="btn btn-phoenix-secondary me-2 mb-2 mb-sm-0" type="button" onClick={onCancel} disabled={saving}>キャンセル</button>
                            <button className="btn btn-primary me-2 mb-2 mb-sm-0" type="submit" disabled={saving}>{saving ? '更新中…' : '更新'}</button>
                        </div>
                    </div>

                    {loading && <div className="text-body-secondary mb-3">読み込み中…</div>}
                    {!loading && error && <div className="text-danger mb-3">{error}</div>}

                    <div className="row g-5">
                        <div className="col-12 col-xl-8">
                            {/* IDs */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">ポイント操作ID</h4>
                                        <input className="form-control" type="text" value={pointActionId} disabled />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">イベントID</h4>
                                        <input className="form-control" type="text" value={eventId} disabled />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">スポットID</h4>
                                        <input className="form-control" type="text" value={spotId} disabled />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">会員番号</h4>
                                        <input className="form-control" type="text" value={userNumber} disabled />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">操作日時</h4>
                                        <input className="form-control" type="text" value={actedAtDisp} disabled />
                                    </div>
                                </div>
                            </div>

                            {/* 操作区分 -> Select */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className={`mb-4 ${invalid('pointActionType')}`} data-error-key="pointActionType">
                                        <h4 className="mb-3">操作区分<span className="text-danger">*</span></h4>
                                        <select
                                            id="pointActionType"
                                            className={`form-select ${invalid('pointActionType')}`}
                                            value={pointActionType}
                                            onChange={(e) => setPointActionType(e.target.value)}
                                            disabled={saving}
                                        >
                                            <option value="">選択してください</option>
                                            {Object.entries(TYPE_LABELS).map(([v, label]) => (
                                                <option key={v} value={v}>{label}</option>
                                            ))}
                                        </select>
                                        {feedback('pointActionType')}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="mb-4">
                                        <h4 className="mb-3">ポイント操作区分詳細</h4>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={pointActionTypeDetail}
                                            onChange={(e) => setPointActionTypeDetail(e.target.value)}
                                            disabled={saving}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 摘要 & ポイント数 */}
                            <div className="row">
                                <div className="col-md-6">
                                    <div className={`mb-4 ${invalid('actionNote')}`} data-error-key="actionNote">
                                        <h4 className="mb-3">操作摘要<span className="text-danger">*</span></h4>
                                        <input
                                            id="actionNote"
                                            className={`form-control ${invalid('actionNote')}`}
                                            type="text"
                                            value={actionNote}
                                            onChange={(e) => setActionNote(e.target.value)}
                                            disabled={saving}
                                        />
                                        {feedback('actionNote')}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className={`mb-4 ${invalid('amount')}`} data-error-key="amount">
                                        <h4 className="mb-3">ポイント数<span className="text-danger">*</span></h4>
                                        <input
                                            id="amount"
                                            className={`form-control ${invalid('amount')}`}
                                            type="number"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            disabled={saving}
                                        />
                                        {feedback('amount')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* right column */}
                        <div className="col-12 col-xl-4">
                            <div className="row g-2">
                                <div className="col-12">
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row gx-3">
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className={`mb-4 ${invalid('pointStatus')}`} data-error-key="pointStatus">
                                                        <h5 className="mb-3">ポイントステータス<span className="text-danger">*</span></h5>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="point_status"
                                                                    value="0"
                                                                    checked={pointStatus === '0'}
                                                                    onChange={() => setPointStatus('0')}
                                                                    disabled={saving}
                                                                /> 予約
                                                            </label>
                                                            <label className="mb-0">
                                                                <input
                                                                    type="radio"
                                                                    name="point_status"
                                                                    value="1"
                                                                    checked={pointStatus === '1'}
                                                                    onChange={() => setPointStatus('1')}
                                                                    disabled={saving}
                                                                /> 確定
                                                            </label>
                                                        </div>
                                                        {feedback('pointStatus')}
                                                    </div>
                                                </div>
                                                <div className="col-12 col-sm-6 col-xl-12">
                                                    <div className="mb-4">
                                                        <div className="d-flex flex-wrap mb-2">
                                                            <h5 className="mb-0 text-body-highlight me-2">アクション実施日時</h5>
                                                        </div>
                                                        <div className="input-group">
                                                            <input
                                                                id="action_implement_time"
                                                                className="form-control"
                                                                type="text"
                                                                value={actionImplementText}
                                                                readOnly
                                                            />
                                                            <span
                                                                className="input-group-text"
                                                                role="button"
                                                                tabIndex={0}
                                                                onClick={() => { setActionImplementDate(null); try { actionFpRef.current?.clear(); } catch { } }}
                                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setActionImplementDate(null); try { actionFpRef.current?.clear(); } catch { } } }}
                                                                title="クリア"
                                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                            >×</span>
                                                        </div>
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                             
                        </div>
                    </div>
                </form>
            </>
        </Layout>
    );
}
